/**
 * Zod schemas for each Spark step.
 *
 * The shapes here mirror `SparkDraft` so validation lives in one
 * place. React Hook Form wires `zodResolver(schema)` per step; the
 * wizard store holds the accumulated draft.
 */

import { z } from 'zod';

// Domain tag + the 8 built-in domains plus `Custom`.
export const DOMAIN_VALUES = [
  'Audio',
  'Process',
  'Medical',
  'Financial',
  'Defense',
  'Government',
  'Generic',
] as const;

const domainSchema = z.enum(DOMAIN_VALUES);

// Step 1 — Basic identity.
export const step1Schema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'Name must be at least 2 characters.')
    .max(64, 'Name must be at most 64 characters.')
    .regex(/^[a-z0-9][a-z0-9_-]*$/i, 'Letters, digits, _ and - only.'),
  description: z
    .string()
    .trim()
    .min(4, 'Describe what this GI does.')
    .max(280, 'Description must be at most 280 characters.'),
  version: z
    .string()
    .trim()
    .regex(
      /^(?:0|[1-9]\d*)\.(?:0|[1-9]\d*)\.(?:0|[1-9]\d*)$/,
      'Use semantic versioning, e.g. 1.0.0.',
    ),
  domain: domainSchema,
  author_name: z.string().trim().min(1, 'Author name is required.').max(64),
});

// A single port definition (shared by inputs and outputs).
const giTypeSchema = z.enum([
  'Stream',
  'Event',
  'Document',
  'Signal',
  'Tensor',
  'Value',
  'Binary',
]);

export const portSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Port name is required.')
    .max(64)
    .regex(/^[a-z0-9][a-z0-9_-]*$/i, 'Letters, digits, _ and - only.'),
  data_type: giTypeSchema,
  required: z.boolean(),
  schema_text: z
    .string()
    .trim()
    .default('')
    .refine((s) => {
      if (s === '') return true;
      try {
        JSON.parse(s);
        return true;
      } catch {
        return false;
      }
    }, 'Schema must be valid JSON or empty.'),
});

export const step2Schema = z.object({
  inputs: z
    .array(portSchema)
    .min(0)
    .max(16)
    .superRefine((ports, ctx) => assertUnique(ports, ctx, 'input port')),
});

export const step3Schema = z.object({
  outputs: z
    .array(portSchema)
    .min(1, 'At least one output is required for the GI to have observable behavior.')
    .max(16)
    .superRefine((ports, ctx) => assertUnique(ports, ctx, 'output port')),
});

// Parameter: kind-tagged default + optional range.
export const parameterKindSchema = z.enum(['Integer', 'Float', 'Bool', 'String', 'Enum']);

export const parameterSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'Parameter name is required.')
      .max(64)
      .regex(/^[a-z0-9][a-z0-9_-]*$/i, 'Letters, digits, _ and - only.'),
    description: z.string().trim().max(280).default(''),
    kind: parameterKindSchema,
    default_text: z.string().trim().min(1, 'A default value is required.'),
    range_min: z.string().trim().default(''),
    range_max: z.string().trim().default(''),
    enum_variants: z.string().trim().default(''), // comma-separated when kind=Enum
  })
  .superRefine((p, ctx) => {
    // Validate the default against the declared kind.
    switch (p.kind) {
      case 'Integer': {
        if (!/^-?\d+$/.test(p.default_text)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['default_text'],
            message: 'Default must be a whole number.',
          });
        }
        assertNumericRange(p.range_min, p.range_max, /^-?\d+$/, 'whole number', ctx);
        break;
      }
      case 'Float': {
        if (!Number.isFinite(Number(p.default_text))) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['default_text'],
            message: 'Default must be a finite number.',
          });
        }
        assertNumericRange(p.range_min, p.range_max, /^-?\d+(?:\.\d+)?$/, 'number', ctx);
        break;
      }
      case 'Bool': {
        if (p.default_text !== 'true' && p.default_text !== 'false') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['default_text'],
            message: 'Default must be "true" or "false".',
          });
        }
        break;
      }
      case 'String': {
        if (p.default_text.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['default_text'],
            message: 'Default string must be non-empty.',
          });
        }
        break;
      }
      case 'Enum': {
        const variants = p.enum_variants
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean);
        if (variants.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['enum_variants'],
            message: 'Enum parameters need at least one variant.',
          });
        } else if (!variants.includes(p.default_text)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['default_text'],
            message: 'Default must be one of the declared variants.',
          });
        }
        break;
      }
    }
  });

export const step4Schema = z.object({
  parameters: z
    .array(parameterSchema)
    .max(32)
    .superRefine((params, ctx) => assertUnique(params, ctx, 'parameter')),
});

// Step 5 has no per-form Zod shape — the file drop handles its own
// validation via the browser File API. But we validate the resulting
// hash here so downstream steps get a typed guarantee.
export const hash256Schema = z
  .string()
  .regex(/^sha256:[a-f0-9]{64}$/, 'Hash must be sha256:<64 hex>.');

export type Step1Values = z.infer<typeof step1Schema>;
export type Step2Values = z.infer<typeof step2Schema>;
export type Step3Values = z.infer<typeof step3Schema>;
export type Step4Values = z.infer<typeof step4Schema>;
export type PortValues = z.infer<typeof portSchema>;
export type ParameterValues = z.infer<typeof parameterSchema>;

// ─── Helpers ────────────────────────────────────────────────────────

function assertUnique(
  items: Array<{ name: string }>,
  ctx: z.RefinementCtx,
  kind: string,
) {
  const seen = new Set<string>();
  items.forEach((p, i) => {
    if (seen.has(p.name)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [i, 'name'],
        message: `Duplicate ${kind} name "${p.name}".`,
      });
    }
    seen.add(p.name);
  });
}

function assertNumericRange(
  min: string,
  max: string,
  pattern: RegExp,
  label: string,
  ctx: z.RefinementCtx,
) {
  if (min === '' && max === '') return;
  if (min !== '' && !pattern.test(min)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['range_min'],
      message: `Min must be a ${label}.`,
    });
  }
  if (max !== '' && !pattern.test(max)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['range_max'],
      message: `Max must be a ${label}.`,
    });
  }
  if (min !== '' && max !== '' && Number(min) > Number(max)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['range_max'],
      message: 'Max must be ≥ min.',
    });
  }
}
